import React, { useRef, useEffect, useState } from 'react';
import { GameState, Position, Poop, GameConfig } from '../types/GameTypes';
import webSocketService from '../services/WebSocketService';
import soundService from '../services/SoundService';
// Import dragon and poop sprites
import dragon1 from '../assets/images/dragon_1.webp';
import dragon2 from '../assets/images/dragon_2.webp';
import dragon3 from '../assets/images/dragon_3.webp';
import dragon4 from '../assets/images/dragon_4.webp';
import dragon5 from '../assets/images/dragon_5.webp';
import dragon6 from '../assets/images/dragon_6.webp';
import poop1 from '../assets/images/poop_1.webp';
import poop2 from '../assets/images/poop_2.webp';
import poop3 from '../assets/images/poop_3.webp';
import backgroundImage from '../assets/images/background.webp';
import ballImage from '../assets/images/ball.webp';
// Import food sprites
import foodFish from '../assets/images/food_fish.webp';
import foodMeat from '../assets/images/food_meet.webp';
import foodCheese from '../assets/images/food_cheese.webp';
// Import death asset (placeholder)
import tombstoneImage from '../assets/images/tombstone.webp'; 

// Define size of game elements
const DRAGON_SIZE = 100;
const POOP_SIZE = 100;
const BALL_SIZE = 100;
const FOOD_SIZE = 100;
const TOMBSTONE_SIZE = 150; // Size for the tombstone

// Array of dragon sprites
const dragonSprites = [dragon1, dragon2, dragon3, dragon4, dragon5, dragon6];
// Array of poop sprites
const poopSprites = [poop1, poop2, poop3];
// Array of food sprites
const foodSprites = [foodFish, foodMeat, foodCheese];

// Constants for ball wiggle animation
const WIGGLE_INTERVAL = 3000; // Time in ms between wiggles
const WIGGLE_DURATION = 1000; // Duration of each wiggle in ms
const WIGGLE_AMPLITUDE = 5; // Maximum angle of wiggle rotation
const EATING_ANIMATION_DURATION = 2000; // Duration of eating animation in ms

// Constants for poop wiggle animation
const POOP_WIGGLE_INTERVAL = 5000; // Time in ms between poop wiggles
const POOP_WIGGLE_DURATION = 1500; // Duration of each poop wiggle in ms
const POOP_WIGGLE_AMPLITUDE = 10; // Maximum angle of poop wiggle rotation

// Constants for food animation
const FOOD_WIGGLE_AMPLITUDE = 10; // Add larger amplitude for food wiggle
const FOOD_GLOW_INTENSITY = 25; // Add stronger glow intensity for food

// Add necessary state variables and constants for food wiggling
const FOOD_WIGGLE_INTERVAL = 4000; // Time in ms between food wiggles
const FOOD_WIGGLE_DURATION = 1200; // Duration of each food wiggle in ms

// Constants for Auto-Feature Effects
const SPARKLE_DURATION = 500; // ms
const FIREBALL_DURATION = 800; // ms
const FIREBALL_MAX_RADIUS = 50; // pixels

// Animation state for different game elements
enum AnimationState {
  IDLE = 'idle',
  EATING = 'eating',
  PLAYING = 'playing',
  POOPING = 'pooping'
}

interface GameCanvasProps {
  gameState: GameState | null;
}

const GameCanvas: React.FC<GameCanvasProps> = ({ gameState }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const animationRef = useRef<number>(0);
  const [ballMoving, setBallMoving] = useState<boolean>(false);
  const [ballPosition, setBallPosition] = useState<Position>({ x: 0, y: 0 });
  const [targetBallPosition, setTargetBallPosition] = useState<Position>({ x: 0, y: 0 });
  // Add rotation for ball
  const [ballRotation, setBallRotation] = useState<number>(0);
  // Add rotation speed for continuous rotation while moving
  const ballRotationSpeed = 5;
  // Add wiggle state for ball when idle
  const [ballWiggling, setBallWiggling] = useState<boolean>(false);
  const [ballWiggleStartTime, setBallWiggleStartTime] = useState<number>(0);
  const [lastWiggleTime, setLastWiggleTime] = useState<number>(0);
  // Poop wiggle states
  const [wigglingPoopId, setWigglingPoopId] = useState<string | null>(null);
  const [poopWiggleStartTime, setPoopWiggleStartTime] = useState<number>(0);
  const [lastPoopWiggleTime, setLastPoopWiggleTime] = useState<number>(0);
  const [poopRotations, setPoopRotations] = useState<Map<string, number>>(new Map());
  // Dragon sprite states
  const [currentDragonSpriteIndex, setCurrentDragonSpriteIndex] = useState<number>(0);
  const [nextDragonSpriteIndex, setNextDragonSpriteIndex] = useState<number>(1);
  const [blendFactor, setBlendFactor] = useState<number>(0);
  const [dragonPosition, setDragonPosition] = useState<Position>({ x: 0, y: 0 });
  const [targetDragonPosition, setTargetDragonPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragonMoving, setIsDragonMoving] = useState<boolean>(false);
  // Add sine wave animation timers for dragon
  const [sineWaveTimer, setSineWaveTimer] = useState<number>(0);
  // Poop sprite index mapping
  const poopSpriteMap = useRef<Map<string, number>>(new Map());
  // Loaded image refs
  const dragonImagesRef = useRef<HTMLImageElement[]>([]);
  const poopImagesRef = useRef<HTMLImageElement[]>([]);
  const ballImageRef = useRef<HTMLImageElement>(new Image());
  const foodImagesRef = useRef<HTMLImageElement[]>([]);
  const [currentFoodIndex, setCurrentFoodIndex] = useState<number>(0);
  const [backgroundLoaded, setBackgroundLoaded] = useState<boolean>(false);
  const backgroundImageRef = useRef<HTMLImageElement>(new Image());
  const tombstoneImageRef = useRef<HTMLImageElement>(new Image()); // Added ref for tombstone

  // Add state for pulsing effect
  const [pulseValue, setPulseValue] = useState<number>(0);
  // Add state for hovering over any interactable element (ball or poop)
  const [isHoveringOverInteractable, setIsHoveringOverInteractable] = useState<boolean>(false);
  // Add state for dragon animation
  const [dragonAnimationState, setDragonAnimationState] = useState<AnimationState>(AnimationState.IDLE);
  const [animationEndTime, setAnimationEndTime] = useState<number>(0);
  // Add food wiggle states
  const [foodWiggling, setFoodWiggling] = useState<boolean>(false);
  const [foodWiggleStartTime, setFoodWiggleStartTime] = useState<number>(0);
  const [lastFoodWiggleTime, setLastFoodWiggleTime] = useState<number>(0);
  const [foodRotation, setFoodRotation] = useState<number>(0);

  // State for Auto-Feature Effects
  const [sparkles, setSparkles] = useState<{ id: number, x: number, y: number, startTime: number }[]>([]);
  const [fireball, setFireball] = useState<{ x: number, y: number, startTime: number, maxRadius: number } | null>(null);

  // Render functions
  const renderBackground = (ctx: CanvasRenderingContext2D) => {
    if (backgroundLoaded) {
      ctx.drawImage(backgroundImageRef.current, 0, 0, canvasSize.width, canvasSize.height);
    }
  };

  const renderDragon = (ctx: CanvasRenderingContext2D, gameState: GameState) => {
    // Map the backend X position to the canvas coordinates using the config boundaries
    let dragonX = dragonPosition.x ?? canvasSize.width / 2;
    let dragonY = dragonPosition.y ?? canvasSize.height / 2;
    
    // If we have config, use it to map the position from backend coordinates to canvas
    const currentDragonImage = dragonImagesRef.current[currentDragonSpriteIndex];
    const nextDragonImage = dragonImagesRef.current[nextDragonSpriteIndex];
    
    if (currentDragonImage && currentDragonImage.complete) {
      // Apply rotation if dragon is moving
      ctx.save();
      ctx.translate(dragonX, dragonY);
      
      // Apply rotation from the dragon position
      if (dragonPosition.rotation !== undefined) {
        ctx.rotate((dragonPosition.rotation * Math.PI) / 180);
      }
      
      // If dragon is eating, make it slightly larger for the animation
      const size = DRAGON_SIZE * (dragonAnimationState === AnimationState.EATING ? 2.7 : 2.5);
      
      // Draw the current dragon sprite with proper alpha
      ctx.globalAlpha = 1 - blendFactor;
      ctx.drawImage(
        currentDragonImage,
        -size / 2,
        -size / 2,
        size,
        size
      );
      
      // Draw the next dragon sprite with proper alpha for blending
      if (isDragonMoving && nextDragonImage && nextDragonImage.complete) {
        ctx.globalAlpha = blendFactor;
        ctx.drawImage(
          nextDragonImage,
          -size / 2,
          -size / 2,
          size,
          size
        );
      }
      
      // Reset the global alpha
      ctx.globalAlpha = 1;
      ctx.restore();
    }
  };

  // Render a poop
  const renderPoop = (
    ctx: CanvasRenderingContext2D, 
    poop: Poop,
    config?: GameConfig
  ) => {
    // Get or create a random poop sprite index for this poop
    if (!poopSpriteMap.current.has(poop.id)) {
      const randomPoopIndex = Math.floor(Math.random() * poopSprites.length);
      poopSpriteMap.current.set(poop.id, randomPoopIndex);
    }
    
    const poopSpriteIndex = poopSpriteMap.current.get(poop.id) || 0;
    const poopImage = poopImagesRef.current[poopSpriteIndex];
    
    if (poopImage && poopImage.complete) {
      // Get rotation for this poop if it's wiggling
      const rotation = poopRotations.get(poop.id) || 0;
      
      ctx.save();
      ctx.translate(poop.position.x, poop.position.y);
      
      // Apply rotation if this poop is wiggling
      if (rotation !== 0) {
        ctx.rotate((rotation * Math.PI) / 180);
      }
      
      ctx.drawImage(
        poopImage,
        -POOP_SIZE / 2,
        -POOP_SIZE / 2,
        POOP_SIZE,
        POOP_SIZE
      );
      
      ctx.restore();
    }
  };

  // Separate ball rendering function
  const renderBall = (ctx: CanvasRenderingContext2D) => {
    let ballX = ballPosition.x;
    let ballY = ballPosition.y;

    if (ballImageRef.current && ballImageRef.current.complete) {
      ctx.save();
      ctx.translate(ballX, ballY);
      
      // Add glow effect when not moving (always active except when in motion)
      if (!ballMoving) {
        ctx.shadowColor = `rgba(${255}, ${0}, ${0}, ${255})`; // Dynamic color with alpha
        ctx.shadowBlur = pulseValue;
      }
      
      // Apply rotation to the ball
      if (ballRotation !== undefined) {
        ctx.rotate((ballRotation * Math.PI) / 180);
      }
      
      ctx.drawImage(
        ballImageRef.current,
        -BALL_SIZE / 2,
        -BALL_SIZE / 2,
        BALL_SIZE,
        BALL_SIZE
      );
      
      ctx.restore();
    }
  };

  // Render food
  const renderFood = (
    ctx: CanvasRenderingContext2D,
    position: Position
  ) => {
    const foodImage = foodImagesRef.current[currentFoodIndex];
    
    if (foodImage && foodImage.complete) {
      ctx.save();
      ctx.translate(position.x, position.y);
      
      // Remove floating animation based on sineWaveTimer
      // const floatOffset = Math.sin(sineWaveTimer * 0.05) * FOOD_WIGGLE_AMPLITUDE; 
      
      ctx.shadowColor = "rgba(255, 255, 255, 1)";
      ctx.shadowBlur = FOOD_GLOW_INTENSITY + pulseValue;

      // Apply wiggle rotation based on state
      ctx.rotate((foodRotation * Math.PI) / 180);
      
      ctx.drawImage(
        foodImage,
        -FOOD_SIZE / 2,
        -FOOD_SIZE / 2, // Remove floatOffset from Y position
        FOOD_SIZE,
        FOOD_SIZE
      );
      
      ctx.restore();
    }
  };

  // Render Tombstone when dragon is not alive
  const renderTombstone = (ctx: CanvasRenderingContext2D) => {
    if (tombstoneImageRef.current && tombstoneImageRef.current.complete) {
      // Position the tombstone in the center for now
      const tombstoneX = canvasSize.width / 2;
      const tombstoneY = canvasSize.height / 2 + 50; // Slightly lower than center

      ctx.save();
      ctx.translate(tombstoneX, tombstoneY);
      ctx.drawImage(
        tombstoneImageRef.current,
        -TOMBSTONE_SIZE / 2,
        -TOMBSTONE_SIZE / 2,
        TOMBSTONE_SIZE,
        TOMBSTONE_SIZE
      );
      ctx.restore();
    }
  };

  // Animation effect
  useEffect(() => {
    const animate = () => {
      if (canvasRef.current && gameState) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          // Clear the canvas
          ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
          
          // Render the game elements in the correct order:
          // 1. Background
          renderBackground(ctx);
          
          // 2. Poops
          if (gameState.objects.poops.length > 0) {
            gameState.objects.poops.forEach(poop => {
              renderPoop(ctx, poop, gameState.config);
            });
          }
          
          // 3. Dragon or Tombstone
          if (gameState.dragon.isAlive) {
            renderDragon(ctx, gameState);
          } else {
            renderTombstone(ctx);
          }
          
          // 4. Food (if exists) - moved after dragon to be in front
          if (gameState.objects.food.exists) {
            renderFood(ctx, gameState.objects.food.position);
          }
          
          // 5. Ball (render last to appear in front)
          renderBall(ctx);

          // 6. Render effects on top
          renderEffects(ctx);
        }
      }
      // Animate dragon movement if it's moving
      if (isDragonMoving) {
        // Calculate the direction vector in backend coordinates
        const dx = targetDragonPosition.x - dragonPosition.x;
        const dy = targetDragonPosition.y - dragonPosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 5) {
          // Important: Use the exact target position when we reach it
          const finalPosition = {
            x: targetDragonPosition.x,
            y: targetDragonPosition.y,
            rotation: targetDragonPosition.rotation
          };
          
          setDragonPosition(finalPosition);
          setIsDragonMoving(false);
          
          // Reset blend animation
          setBlendFactor(0);
          setCurrentDragonSpriteIndex(nextDragonSpriteIndex);
        } else {
          // Move toward target at a fixed speed in backend coordinates
          const speed = 1.5; // Slower speed for smoother movement
          const ratio = speed / distance;
          
          const sineRotation = Math.sin(sineWaveTimer) * 0.5;
          
          const new_pos = {
            x: dragonPosition.x + dx * ratio,
            y: dragonPosition.y + dy * ratio,
            // Add the sine wave rotation to the base rotation
            rotation: (dragonPosition.rotation || 0) + sineRotation
          }
          
          setDragonPosition(new_pos);
          
          // Update blend factor for sprite animation
          setBlendFactor(prev => Math.min(prev + 0.05, 1));
        }
      }
      
      // Update sine wave timer for continuous animations (like food wiggle)
      setSineWaveTimer(prev => prev + 0.1);
      
      // Handle ball wiggle animation when idle
      const currentTime = Date.now();
      if (!ballMoving) {
        if (!ballWiggling && currentTime - lastWiggleTime > WIGGLE_INTERVAL) {
          // Start a new wiggle
          setBallWiggling(true);
          setBallWiggleStartTime(currentTime);
          setLastWiggleTime(currentTime);
        }
        
        if (ballWiggling) {
          const wiggleElapsed = currentTime - ballWiggleStartTime;
          if (wiggleElapsed < WIGGLE_DURATION) {
            // Calculate wiggle effect using sine wave
            const progress = wiggleElapsed / WIGGLE_DURATION;
            const wiggleAngle = Math.sin(progress * Math.PI * 4) * WIGGLE_AMPLITUDE;
            setBallRotation(wiggleAngle);
          } else {
            // Wiggle completed
            setBallWiggling(false);
            setBallRotation(0);
          }
        }
      }
      
      // Handle poop wiggle animation
      if (gameState && gameState.objects.poops && gameState.objects.poops.length > 0) {
        // Check if we need to start a new poop wiggle
        if (wigglingPoopId === null && currentTime - lastPoopWiggleTime > POOP_WIGGLE_INTERVAL) {
          // Select a random poop to wiggle
          const randomIndex = Math.floor(Math.random() * gameState.objects.poops.length);
          const randomPoopId = gameState.objects.poops[randomIndex].id;
          
          setWigglingPoopId(randomPoopId);
          setPoopWiggleStartTime(currentTime);
          setLastPoopWiggleTime(currentTime);
        }
        
        // If a poop is wiggling, animate it
        if (wigglingPoopId !== null) {
          const wiggleElapsed = currentTime - poopWiggleStartTime;
          
          if (wiggleElapsed < POOP_WIGGLE_DURATION) {
            // Calculate wiggle effect using sine wave
            const progress = wiggleElapsed / POOP_WIGGLE_DURATION;
            const wiggleAngle = Math.sin(progress * Math.PI * 6) * POOP_WIGGLE_AMPLITUDE;
            
            // Update the rotations map with just the wiggling poop's rotation
            const newRotations = new Map(poopRotations);
            newRotations.set(wigglingPoopId, wiggleAngle);
            setPoopRotations(newRotations);
          } else {
            // Wiggle completed
            const newRotations = new Map(poopRotations);
            newRotations.delete(wigglingPoopId);
            setPoopRotations(newRotations);
            setWigglingPoopId(null);
          }
        }
      }
      
      // Handle food wiggle animation when food exists
      if (gameState?.objects.food.exists) {
        const currentTime = Date.now();
        
        // Check if we need to start a new food wiggle
        if (!foodWiggling && currentTime - lastFoodWiggleTime > FOOD_WIGGLE_INTERVAL) {
          setFoodWiggling(true);
          setFoodWiggleStartTime(currentTime);
          setLastFoodWiggleTime(currentTime);
        }
        
        // If food is wiggling, animate it
        if (foodWiggling) {
          const wiggleElapsed = currentTime - foodWiggleStartTime;
          if (wiggleElapsed < FOOD_WIGGLE_DURATION) {
            // Calculate wiggle effect using sine wave
            const progress = wiggleElapsed / FOOD_WIGGLE_DURATION;
            const wiggleAngle = Math.sin(progress * Math.PI * 4) * FOOD_WIGGLE_AMPLITUDE;
            setFoodRotation(wiggleAngle);
          } else {
            // Wiggle completed
            setFoodWiggling(false);
            setFoodRotation(0);
          }
        }
      } else {
        // Reset food wiggle state if food doesn't exist
        if (foodWiggling) {
          setFoodWiggling(false);
          setFoodRotation(0);
        }
      }
      
      // Animate ball movement
      if (ballMoving) {
        // Reset wiggle state when ball starts moving
        if (ballWiggling) {
          setBallWiggling(false);
        }

        // Similar to dragon movement with rotation
        const dx = targetBallPosition.x - ballPosition.x;
        const dy = targetBallPosition.y - ballPosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 5) { // Close enough to target
          setBallPosition(targetBallPosition);
          setBallMoving(false);
          // Keep the last rotation when ball stops
        } else {
          // Use an easing function (quadratic ease-out)
          // Start fast, end slow
          // Calculate progress as ratio of distance traveled to total distance
          const initialDistance = Math.sqrt(
            Math.pow(targetBallPosition.x - ballPosition.x, 2) + 
            Math.pow(targetBallPosition.y - ballPosition.y, 2)
          );
          
          // Base speed that decreases with distance
          const maxSpeed = 10;
          const minSpeed = 1;
          // Calculate speed based on distance ratio (faster at beginning, slower at end)
          const speedFactor = Math.max(distance / initialDistance, 0.1);
          const speed = minSpeed + (maxSpeed - minSpeed) * speedFactor;
          
          const ratio = speed / distance;
          
          // Determine rotation direction based on x movement
          // If dx < 0, ball is moving left, so rotate counterclockwise (negative)
          // If dx > 0, ball is moving right, so rotate clockwise (positive)
          const rotationDirection = dx < 0 ? -1 : 1;
          
          // Continuously rotate the ball while moving, with correct direction
          setBallRotation(prevRotation => prevRotation + (ballRotationSpeed * rotationDirection));
          
          // Update ball position with the calculated speed
          setBallPosition({
            x: ballPosition.x + dx * ratio,
            y: ballPosition.y + dy * ratio
          });
        }
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [gameState, canvasSize, dragonPosition, ballMoving, targetBallPosition, 
      ballPosition, isDragonMoving, targetDragonPosition, sineWaveTimer,
      pulseValue, isHoveringOverInteractable, ballWiggling, ballWiggleStartTime, lastWiggleTime,
      wigglingPoopId, poopWiggleStartTime, lastPoopWiggleTime, poopRotations,
      foodWiggling, foodWiggleStartTime, lastFoodWiggleTime, foodRotation,
      renderBackground, renderDragon, renderPoop, renderBall, renderFood, ballRotationSpeed, nextDragonSpriteIndex, 
      sparkles, fireball]);

  // Animation effect for pulsing
  useEffect(() => {
    const pulseTick = () => {
      setPulseValue(prev => (prev + 0.05) % (2 * Math.PI));
    };
    
    const pulseTimer = setInterval(pulseTick, 50);
    return () => clearInterval(pulseTimer);
  }, []);

  // Animation effect for cycling through dragon sprites during idle state
  useEffect(() => {
    const spriteChangeInterval = setInterval(() => {
      // Only cycle sprites when dragon is idle AND alive
      if (gameState?.dragon.isAlive && dragonAnimationState === AnimationState.IDLE) {
        setCurrentDragonSpriteIndex(nextDragonSpriteIndex);
        
        // Generate a new random dragon sprite index that's different from the current one
        let newNextIndex;
        do {
          newNextIndex = Math.floor(Math.random() * dragonSprites.length);
        } while (newNextIndex === nextDragonSpriteIndex);
        
        setNextDragonSpriteIndex(newNextIndex);
      }
    }, 8000); // Change sprite every 8 seconds
    
    return () => clearInterval(spriteChangeInterval);
  }, [gameState, dragonAnimationState, nextDragonSpriteIndex]); // Added gameState dependency

  // Handle window resize to make the canvas responsive
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current) {
        const container = canvasRef.current.parentElement;
        if (container) {
          const width = 1024//container.clientWidth;
          const height = 1024//Math.min(500, Math.max(300, window.innerHeight * 0.6));
          setCanvasSize({ width, height });
          canvasRef.current.width = width;
          canvasRef.current.height = height;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update ball position from game state
  useEffect(() => {
    if (gameState?.objects.ball) {
      // Only update if we're not currently animating
      if (!ballMoving) {
        setBallPosition({ 
          x: gameState.objects.ball.position.x, 
          y: gameState.objects.ball.position.y 
        });
        
        // Reset ball rotation when ball stops moving
        if (!ballWiggling) {
          setBallRotation(0);
        }
      }
      
      // If the server says the ball is in motion but our local state disagrees,
      // update our local state to match the server
      if (gameState.objects.ball.inMotion && !ballMoving) {
        setBallMoving(true);
      }
    }
  }, [gameState, ballMoving, ballWiggling]);

  // Update dragon position from game state
  useEffect(() => {
    if (gameState?.dragon?.position && dragonPosition.x === 0 && dragonPosition.y === 0) {
      const newPosition = {
        x: gameState.dragon.position.x,
        y: gameState.dragon.position.y,
        rotation: gameState.dragon.position.rotation
      };
      setDragonPosition(newPosition);
    }
  }, [gameState, dragonPosition.x, dragonPosition.y]);

  // Socket event listeners for dragon animations
  useEffect(() => {
    webSocketService.onDragonEat(() => {
      // Set animation state to eating
      setDragonAnimationState(AnimationState.EATING);
      setAnimationEndTime(Date.now() + EATING_ANIMATION_DURATION);
      
      // Play eating sound
      soundService.playEatingSound();
    });

    webSocketService.onDragonKickBall((position) => {
      // Play the kick sound when the ball is actually kicked
      soundService.playBallKickSound();
      
      // Set target position and start ball moving animation
      setTargetBallPosition(position);
      setBallMoving(true);
    });
    
    webSocketService.onDragonMove((position) => {
      // Start dragon movement animation
      setTargetDragonPosition({
        x: position.x,
        y: position.y,
        rotation: position.rotation
      });
      
      setIsDragonMoving(true);
      
      // Generate new random dragon sprite indices for blending animation
      const newSpriteIndex = Math.floor(Math.random() * dragonSprites.length);
      let nextSpriteIndex;
      do {
        nextSpriteIndex = Math.floor(Math.random() * dragonSprites.length);
      } while (nextSpriteIndex === newSpriteIndex);
      
      // Set the new sprite indices
      setCurrentDragonSpriteIndex(newSpriteIndex);
      setNextDragonSpriteIndex(nextSpriteIndex);
      setBlendFactor(0);
    });

    // Listen for Auto-Feature activation events
    webSocketService.onAutoCleanActivate((data) => {
      console.log("AutoClean Activated - Triggering Fireball Effect");
      soundService.playFireballSound(); // Placeholder sound
      // Start fireball effect from dragon's position
      setFireball({
        x: dragonPosition.x,
        y: dragonPosition.y,
        startTime: Date.now(),
        maxRadius: FIREBALL_MAX_RADIUS,
      });
    });

    webSocketService.onAutoPlayActivate(() => {
      console.log("AutoPlay Activated - Triggering Sparkle Effect");
      soundService.playSparkleSound(); // Placeholder sound
      // Add sparkles around the dragon
      triggerSparkleEffect(dragonPosition.x, dragonPosition.y);
    });

    webSocketService.onAutoFeedActivate(() => {
      console.log("AutoFeed Activated - Triggering Sparkle Effect");
      soundService.playSparkleSound(); // Placeholder sound
      // Add sparkles around the dragon
      triggerSparkleEffect(dragonPosition.x, dragonPosition.y);
    });

    return () => {
      // No need to clean up the socket listeners as the service is a singleton
    };
  }, []);

  // Animation effect to handle animation state transitions
  useEffect(() => {
    // Create an interval to check if animations should end
    const animationCheckInterval = setInterval(() => {
      const currentTime = Date.now();
      
      // Check if any animation should end
      if (dragonAnimationState !== AnimationState.IDLE && currentTime > animationEndTime) {
        setDragonAnimationState(AnimationState.IDLE);
      }
    }, 100);
    
    return () => {
      clearInterval(animationCheckInterval);
    };
  }, [dragonAnimationState, animationEndTime]);

  // Load all sprite images
  useEffect(() => {
    // Load dragon images
    const dragonImages: HTMLImageElement[] = [];
    dragonSprites.forEach((sprite, index) => {
      const img = new Image();
      img.src = sprite;
      dragonImages.push(img);
    });
    dragonImagesRef.current = dragonImages;
    
    // Load poop images
    const poopImages: HTMLImageElement[] = [];
    poopSprites.forEach((sprite) => {
      const img = new Image();
      img.src = sprite;
      poopImages.push(img);
    });
    poopImagesRef.current = poopImages;
    
    // Load food images
    const foodImages: HTMLImageElement[] = [];
    foodSprites.forEach((sprite) => {
      const img = new Image();
      img.src = sprite;
      foodImages.push(img);
    });
    foodImagesRef.current = foodImages;
    
    // Load ball image
    ballImageRef.current.src = ballImage;
    
    // Load tombstone image
    tombstoneImageRef.current.src = tombstoneImage;
    
    // Load background image
    backgroundImageRef.current.src = backgroundImage;
    backgroundImageRef.current.onload = () => {
      setBackgroundLoaded(true);
    };
  }, []);

  // Track food appearance to randomize the food type
  useEffect(() => {
    if (gameState?.objects.food.exists) {
      // When food appears, randomly select a food type
      const randomFoodIndex = Math.floor(Math.random() * foodSprites.length);
      setCurrentFoodIndex(randomFoodIndex);
    }
  }, [gameState?.objects.food.exists]);

  // Handle canvas click
  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    // Disable clicks if game state is null or dragon is dead
    if (!canvasRef.current || !gameState || !gameState.dragon.isAlive) {
      console.log("Canvas click disabled");
      return
    };
    
    const rect = canvasRef.current.getBoundingClientRect();

    // map the click to canvas coordinates
    const scaleX = canvasSize.width / rect.width;
    const scaleY = canvasSize.height / rect.height;
    
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    console.log(x, y);
    
    // Check if clicked on food
    if (gameState.objects.food.exists) {
      const foodX = gameState.objects.food.position.x;
      const foodY = gameState.objects.food.position.y;
      
      const dx = foodX - x;
      const dy = foodY - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < FOOD_SIZE / 2) {
        console.log("Clicked on food");
        // Remove food and trigger dragon movement
        // The server will handle the dragonEat event
        webSocketService.dragonEatFood();
        return;
      }
    }

    // Check if clicked on a poop
    for (const poop of gameState.objects.poops) {
      // For poops, we need to map the poop positions from backend to canvas coordinates for hit detection
      let poopX = poop.position.x;
      let poopY = poop.position.y;
      
      const dx = poopX - x;
      const dy = poopY - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < POOP_SIZE) {
        // Clean the poop
        webSocketService.cleanPoop(poop.id);
        return;
      }
    }

    // check if ball is clicked
    const dx = ballPosition.x - x;
    const dy = ballPosition.y - y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance < BALL_SIZE) {
      webSocketService.kickBall();
    }
  };

  // Mouse move handler to check if hovering over interactable elements
  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    // Disable hover effects if game state is null or dragon is dead
    if (!canvasRef.current || !gameState || !gameState.dragon.isAlive) {
      // Ensure hover state is false if dead
      if (isHoveringOverInteractable) {
        setIsHoveringOverInteractable(false);
      }
      return;
    }
    
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasSize.width / rect.width;
    const scaleY = canvasSize.height / rect.height;
    
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    
    // Check if mouse is over food
    let hoveringOverFood = false;
    if (gameState.objects.food.exists) {
      const foodX = gameState.objects.food.position.x;
      const foodY = gameState.objects.food.position.y;
      
      const dx = foodX - x;
      const dy = foodY - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < FOOD_SIZE / 2) {
        hoveringOverFood = true;
      }
    }
    
    // Check if mouse is over the ball
    const ballDx = ballPosition.x - x;
    const ballDy = ballPosition.y - y;
    const ballDistance = Math.sqrt(ballDx * ballDx + ballDy * ballDy);
        
    // Check if hovering over any poop
    let hoveringOverPoop = false;
    for (const poop of gameState.objects.poops) {
      const poopX = poop.position.x;
      const poopY = poop.position.y;
      
      const dx = poopX - x;
      const dy = poopY - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < POOP_SIZE) {
        hoveringOverPoop = true;
        break;
      }
    }
    
    // Update general interactable hover state
    setIsHoveringOverInteractable(ballDistance < BALL_SIZE / 2 || hoveringOverPoop || hoveringOverFood);
  };

  // Initialize wiggle timer on component mount
  useEffect(() => {
    // Set initial last wiggle time to current time
    setLastWiggleTime(Date.now());
    setLastPoopWiggleTime(Date.now());
    setLastFoodWiggleTime(Date.now()); // Initialize food wiggle timer
  }, []);

  // Function to trigger sparkle effect
  const triggerSparkleEffect = (x: number, y: number) => {
    const newSparkles: { id: number, x: number, y: number, startTime: number }[] = [];
    for (let i = 0; i < 5; i++) { // Create 5 sparkles
      newSparkles.push({
        id: Date.now() + i, // Simple unique ID
        x: x + (Math.random() - 0.5) * 40, // Random offset around the dragon
        y: y + (Math.random() - 0.5) * 40,
        startTime: Date.now(),
      });
    }
    setSparkles(prev => [...prev, ...newSparkles]);
  };

  // Function to render active effects
  const renderEffects = (ctx: CanvasRenderingContext2D) => {
    const currentTime = Date.now();

    // Render Sparkles
    const activeSparkles = sparkles.filter(s => currentTime < s.startTime + SPARKLE_DURATION);
    setSparkles(activeSparkles); // Update state to remove expired sparkles

    ctx.fillStyle = 'yellow';
    ctx.shadowColor = 'orange';
    ctx.shadowBlur = 10;
    activeSparkles.forEach(sparkle => {
      const elapsed = currentTime - sparkle.startTime;
      const progress = elapsed / SPARKLE_DURATION;
      const alpha = 1 - progress; // Fade out
      const size = 5 * (1 - progress); // Shrink

      if (size <= 0) return;

      ctx.globalAlpha = alpha;
      // Simple star shape (can be improved)
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const angle = (i * Math.PI) / 5;
        const radius = i % 2 === 0 ? size : size / 2;
        ctx.lineTo(sparkle.x + radius * Math.cos(angle), sparkle.y + radius * Math.sin(angle));
      }
      ctx.closePath();
      ctx.fill();
    });
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;

    // Render Fireball (if active)
    if (fireball) {
      const elapsed = currentTime - fireball.startTime;
      if (elapsed < FIREBALL_DURATION) {
        const progress = elapsed / FIREBALL_DURATION;
        const radius = fireball.maxRadius * progress;
        const alpha = 1 - progress;

        ctx.beginPath();
        ctx.arc(fireball.x, fireball.y, radius, 0, 2 * Math.PI);
        
        // Create radial gradient for fire effect
        const gradient = ctx.createRadialGradient(fireball.x, fireball.y, 0, fireball.x, fireball.y, radius);
        gradient.addColorStop(0, `rgba(255, 220, 0, ${alpha * 0.8})`); // Yellow center
        gradient.addColorStop(0.5, `rgba(255, 100, 0, ${alpha * 0.5})`); // Orange mid
        gradient.addColorStop(1, `rgba(255, 0, 0, 0)`); // Transparent outer
        
        ctx.fillStyle = gradient;
        ctx.fill();

      } else {
        setFireball(null); // Fireball animation finished
      }
    }
  };

  return (
    <div className="game-canvas-container w-full bg-white rounded-lg shadow-md overflow-hidden">
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        onClick={handleCanvasClick}
        onMouseMove={handleMouseMove}
        onTouchStart={(e) => {
          // Prevent default behaviors like double-tap zoom
          e.preventDefault();
        }}
        onTouchEnd={()=>{}}
        // Change cursor based on hover state AND alive state
        className={`w-full ${gameState?.dragon.isAlive && isHoveringOverInteractable ? 'cursor-pointer' : 'cursor-default'}`}
      />
    </div>
  );
};

export default GameCanvas; 